import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Flex } from 'lib/components/styles/layout';
import { EmptySearchWrapper } from 'app/pages/admin/landing-pages-v2/styles';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { CarouselContainer, DotsWrapper, ArrowButton, Dot } from './styles';
import { CAROUSEL_ITEMS_PER_PAGE } from '../../aiSpotlight/modal/constants';
import { Typography } from 'react-covideo-common';
import { LoadingIndicator } from 'lib/components';
import { IBackgroundResponse } from 'lib/api/backgrounds/useQueryBackgrounds';
import { FormikHelpers } from 'formik';
import { ICreateSpotlightFormikValues } from '../../aiSpotlight/modal/types';
import { useTheme } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { useIsMutating } from 'react-query';
import { BackgroundCard } from './BackgroundCard';

interface IBackgroundsListProps {
  backgrounds: IBackgroundResponse[];
  selectedBackground: number;
  isLoading: boolean;
  onDefaultBackgroundChange: (
    backgroundId: number,
    formikHelpers?: FormikHelpers<ICreateSpotlightFormikValues>
  ) => void;
  selectedText: string;
  unselectedText: string;
  extendStyles?: {
    list?: {
      wrapperStyles?: CSSProperties;
    };
    card?: {
      cardWrapperStyles?: CSSProperties;
    };
  };
}

export const BackgroundCarousel = ({
  backgrounds,
  selectedBackground,
  isLoading = false,
  extendStyles,
  onDefaultBackgroundChange,
  selectedText,
  unselectedText,
}: IBackgroundsListProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isMutating = useIsMutating();
  const isDisabled = isMutating > 0;
  const isProcessing = isLoading || isDisabled;
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(backgrounds.length / CAROUSEL_ITEMS_PER_PAGE);

  const paginatedBackgrounds = useMemo(() => {
    const start = page * CAROUSEL_ITEMS_PER_PAGE;
    return backgrounds.slice(start, start + CAROUSEL_ITEMS_PER_PAGE);
  }, [backgrounds, page]);

  useEffect(() => {
    const totalPages = Math.ceil(backgrounds.length / CAROUSEL_ITEMS_PER_PAGE);

    if (page >= totalPages && totalPages > 0) {
      setPage(totalPages - 1);
    }

    if (totalPages === 0) {
      setPage(0);
    }
  }, [backgrounds.length, page]);

  const goPrev = () => {
    setPage(prev => Math.max(prev - 1, 0));
  };

  const goNext = () => {
    setPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  return (
    <CarouselContainer>
      <Flex
        width='100%'
        flexDirection='row'
        gap='8px'
        justifyContent='flex-start'
        flexWrap='nowrap'
        style={{
          ...extendStyles?.list?.wrapperStyles,
          position: 'relative',
        }}
      >
        {isLoading ? (
          <div style={{ height: '200px' }}>
            <LoadingIndicator isLoading={isLoading} />
          </div>
        ) : paginatedBackgrounds.length > 0 ? (
          paginatedBackgrounds.map(background => (
            <BackgroundCard
              key={background.backgroundId}
              background={background}
              selectedBackground={
                background.backgroundId === selectedBackground
              }
              isLoading={isProcessing}
              onDefaultBackgroundChange={onDefaultBackgroundChange}
              selectedText={selectedText}
              unselectedText={unselectedText}
              extendStyles={{ ...extendStyles?.card }}
            />
          ))
        ) : (
          <EmptySearchWrapper minHeight='200px'>
            <Typography
              variant='normal'
              fontWeight={600}
              color={colors.neutral[80]}
            >
              {t('There are no Backgrounds available.')}
            </Typography>
          </EmptySearchWrapper>
        )}
      </Flex>

      {!isLoading && (
        <>
          {/* DOTS */}
          {totalPages > 1 && (
            <DotsWrapper>
              {Array.from({ length: totalPages }).map((_, i) => (
                <Dot
                  key={i}
                  active={i === page}
                  onClick={() => setPage(i)}
                  type='button'
                />
              ))}
            </DotsWrapper>
          )}
          {page > 0 && (
            <ArrowButton left onClick={goPrev} type='button'>
              <MdKeyboardArrowLeft size={24} />
            </ArrowButton>
          )}

          {page < totalPages - 1 && (
            <ArrowButton onClick={goNext} type='button'>
              <MdKeyboardArrowRight size={24} />
            </ArrowButton>
          )}
        </>
      )}
    </CarouselContainer>
  );
};
